import React, { useState, useEffect } from 'react';
import { Shield, Award, Clock, Users, AlertCircle } from 'lucide-react';
import { PricingSection } from '../components/PricingSection';
import { TestimonialsSlider } from '../components/TestimonialsSlider';
import { ProcessSlider } from '../components/ProcessSlider';
import { MobileCarousel } from '../components/MobileCarousel';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from '../components/ui/accordion';
import { useLeadForm } from '../hooks/useLeadForm';
import { getCountryCodesForSelect, getDialCodeFromCountry } from '../utils/countryMapping';
import { openChat } from '../utils/zendesk';

export const Home = ({ onOpenPopup }) => {
  const { geo, isLoading, error, submitForm, clearError } = useLeadForm();
  const [heroFormData, setHeroFormData] = useState({
    name: '', email: '', countryCode: '+1', country: 'US', contactNumber: '', message: ''
  });

  const countryCodesOptions = getCountryCodesForSelect();

  useEffect(() => {
    if (!geo?.data?.countryCode) return;
    const cc = geo.data.countryCode;
    const dc = getDialCodeFromCountry(cc);
    setHeroFormData(prev => ({ ...prev, country: cc, countryCode: `+${dc}` }));
  }, [geo]);

  const handleHeroFormSubmit = async (e) => {
    e.preventDefault();
    clearError();
    await submitForm(heroFormData);
  };

  return (
    <div className="min-h-screen overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative bg-[#383D3B] text-white py-12 md:py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/assets/img/banner-bg.jpg"
            alt="Two professionals shaking hands across a table"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#383D3B]/90 via-[#383D3B]/80 to-[#383D3B]/60"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
                Your Brand Deserves Lasting Legal Protection
              </h1>
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-gray-300">
                IP Protection Office pairs you with experienced attorneys who navigate the USPTO filing process so you can focus on growing your business.
              </p>
              <div className="hidden sm:flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={onOpenPopup} data-testid="hero-register-btn"
                  className="bg-[#99AA38] text-white hover:bg-[#889930] text-base md:text-lg px-6 md:px-8 py-5 md:py-6 font-semibold">
                  Register My Trademark
                </Button>
                <Button size="lg" variant="outline" onClick={openChat} data-testid="hero-chat-btn"
                  className="js-chat border-2 border-white text-white hover:bg-white hover:text-[#383D3B] text-base md:text-lg px-6 md:px-8 py-5 md:py-6 font-semibold">
                  Chat Now
                </Button>
              </div>
            </div>

            <div className="order-1 lg:order-2">
              <Card className="p-4 sm:p-6 md:p-8 shadow-2xl bg-white" data-testid="hero-lead-form">
                <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-[#383D3B] mb-2">Secure Your Brand — Talk to an Expert</h2>
                <p className="text-gray-600 mb-4 md:mb-6 text-xs sm:text-sm md:text-base">Share your details and a specialist will reach out within one business day</p>
                
                {error && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-red-800 text-sm">{error}</p>
                  </div>
                )}

                <form onSubmit={handleHeroFormSubmit} className="space-y-4 sm:space-y-5">
                  <div>
                    <Label htmlFor="hero-name" className="text-gray-700">Name *</Label>
                    <Input id="hero-name" type="text" required value={heroFormData.name} onChange={(e) => setHeroFormData({ ...heroFormData, name: e.target.value })} className="mt-1" placeholder="Your full name" disabled={isLoading} />
                  </div>
                  <div>
                    <Label htmlFor="hero-email" className="text-gray-700">Email *</Label>
                    <Input id="hero-email" type="email" required value={heroFormData.email} onChange={(e) => setHeroFormData({ ...heroFormData, email: e.target.value })} className="mt-1" placeholder="Your email address" disabled={isLoading} />
                  </div>
                  {/* {geo?.data?.countryName && (
                    <div>
                      <Label className="text-gray-700">Country</Label>
                      <div className="mt-1 p-3 bg-gray-50 rounded-md border border-gray-200">
                        <p className="text-gray-900 font-medium">{geo.data.countryName}</p>
                      </div>
                    </div>
                  )} */}
                  <div>
                    <Label htmlFor="hero-contact" className="text-gray-700">Contact Number *</Label>
                    <div className="flex gap-2 mt-1">
                      <Select value={heroFormData.countryCode} onValueChange={(value) => { const sc = countryCodesOptions.find(c => c.code === value); setHeroFormData({ ...heroFormData, countryCode: value, country: sc?.countryCode || 'US' }); }} disabled={isLoading}>
                        <SelectTrigger className="w-24 sm:w-32"><SelectValue /></SelectTrigger>
                        <SelectContent>{countryCodesOptions.map((item) => (<SelectItem key={item.code} value={item.code}>{item.code}</SelectItem>))}</SelectContent>
                      </Select>
                      <Input id="hero-contact" type="tel" required value={heroFormData.contactNumber} onChange={(e) => { const v = e.target.value.replace(/[^0-9]/g, ''); if (v.length <= 10) setHeroFormData({ ...heroFormData, contactNumber: v }); }} placeholder="Phone number" className="flex-1" maxLength={10} disabled={isLoading} />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="hero-message" className="text-gray-700">Message</Label>
                    <Textarea id="hero-message" value={heroFormData.message} onChange={(e) => setHeroFormData({ ...heroFormData, message: e.target.value })} className="mt-1" placeholder="Describe your trademark needs..." rows={4} disabled={isLoading} />
                  </div>
                  <Button type="submit" data-testid="hero-form-submit-btn" className="w-full bg-[#99AA38] hover:bg-[#889930] text-white py-5 md:py-6 text-base md:text-lg font-semibold" disabled={isLoading}>
                    {isLoading ? 'Submitting...' : 'Submit Request'}
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Informative Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-stretch max-w-6xl mx-auto">
            <div className="w-full">
              <img
                src="https://images.unsplash.com/photo-1642522029686-5485ea7e6042?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxsYXd5ZXIlMjByZXZpZXdpbmclMjBkb2N1bWVudHMlMjBkZXNrJTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc3NjI4NzUxNXww&ixlib=rb-4.1.0&q=85"
                alt="Professional reviewing legal documents at desk"
                className="w-full h-64 lg:h-full object-cover rounded-2xl shadow-2xl"
              />
            </div>
            <div className="w-full">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#383D3B] mb-4 md:mb-6">
                Safeguarding Brands Is What We Do Best
              </h2>
              <p className="text-base md:text-lg text-gray-700 mb-4 md:mb-6 leading-relaxed">
                At IP Protection Office, we believe every entrepreneur and enterprise should have access to high-quality intellectual property services without the confusion or inflated costs of traditional law firms.
              </p>
              <p className="text-base md:text-lg text-gray-700 mb-4 md:mb-6 leading-relaxed">
                Our legal team has guided thousands of applicants through federal trademark filings, office action responses, and post-registration maintenance. With a proven track record and transparent pricing, you always know what to expect.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  { t: "Licensed Trademark Attorneys", d: "Every application is reviewed by a bar-admitted attorney before submission." },
                  { t: "End-to-End Case Management", d: "Your dedicated specialist handles everything from search to certificate delivery." },
                  { t: "Clear, Upfront Pricing", d: "No surprise fees. Every package details exactly what is included." }
                ].map((item, i) => (
                  <div key={i} className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-[#e8edc7] flex items-center justify-center mt-1">
                      <svg className="w-4 h-4 text-[#99AA38]" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#383D3B] mb-1">{item.t}</h3>
                      <p className="text-gray-600">{item.d}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" onClick={onOpenPopup} className="bg-[#99AA38] hover:bg-[#889930] text-white px-8 py-6 text-lg font-semibold">
                  Register My Trademark
                </Button>
                <Button size="lg" variant="outline" onClick={openChat} className="js-chat border-2 border-[#383D3B] text-[#383D3B] hover:bg-gray-50 px-8 py-6 text-lg font-semibold">
                  Chat Now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="hidden md:grid md:grid-cols-4 gap-8">
            {[
              { v: "10,000+", l: "Trademarks Filed" },
              { v: "98%", l: "Approval Rate" },
              { v: "15+", l: "Years of Practice" },
              { v: "24/7", l: "Client Support" }
            ].map((s, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl font-bold text-[#99AA38] mb-2">{s.v}</div>
                <div className="text-gray-600">{s.l}</div>
              </div>
            ))}
          </div>
          <MobileCarousel autoPlayInterval={3000}>
            {[
              { v: "10,000+", l: "Trademarks Filed" },
              { v: "98%", l: "Approval Rate" },
              { v: "15+", l: "Years of Practice" },
              { v: "24/7", l: "Client Support" }
            ].map((s, i) => (
              <div key={i} className="text-center py-4">
                <div className="text-4xl font-bold text-[#99AA38] mb-2">{s.v}</div>
                <div className="text-gray-600">{s.l}</div>
              </div>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#383D3B] mb-4">Why Clients Choose IP Protection Office</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We combine legal expertise with client-first service to deliver results that matter
            </p>
          </div>
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Shield, t: "Full-Spectrum Protection", d: "From initial search through post-registration monitoring, we cover every angle." },
              { icon: Award, t: "Seasoned Legal Team", d: "Our attorneys bring decades of intellectual property experience to your case." },
              { icon: Clock, t: "Rapid Turnaround", d: "Streamlined workflows and expedited options mean faster filings and fewer delays." },
              { icon: Users, t: "Personal Case Manager", d: "One dedicated point of contact who knows your file inside and out." }
            ].map((c, i) => (
              <Card key={i} className="p-6 text-center hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#e8edc7] text-[#99AA38] mb-4">
                  <c.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-[#383D3B] mb-3">{c.t}</h3>
                <p className="text-gray-600">{c.d}</p>
              </Card>
            ))}
          </div>
          <MobileCarousel>
            {[
              { icon: Shield, t: "Full-Spectrum Protection", d: "From initial search through post-registration monitoring, we cover every angle." },
              { icon: Award, t: "Seasoned Legal Team", d: "Our attorneys bring decades of intellectual property experience to your case." },
              { icon: Clock, t: "Rapid Turnaround", d: "Streamlined workflows and expedited options mean faster filings and fewer delays." },
              { icon: Users, t: "Personal Case Manager", d: "One dedicated point of contact who knows your file inside and out." }
            ].map((c, i) => (
              <Card key={i} className="p-6 text-center shadow-lg">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#e8edc7] text-[#99AA38] mb-4">
                  <c.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-[#383D3B] mb-3">{c.t}</h3>
                <p className="text-gray-600">{c.d}</p>
              </Card>
            ))}
          </MobileCarousel>
        </div>
      </section>

      <ProcessSlider />
      <PricingSection onOpenPopup={onOpenPopup} />
      <TestimonialsSlider />

      {/* FAQ Section */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start max-w-6xl mx-auto">
            <div className="w-full">
              <img
                src="https://images.unsplash.com/photo-1758518729706-b1810dd39cc6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwzfHxidXNpbmVzcyUyMGF0dG9ybmV5JTIwY29uc3VsdGF0aW9uJTIwbWVldGluZ3xlbnwwfHx8fDE3NzYyODQ4ODB8MA&ixlib=rb-4.1.0&q=85"
                alt="Business professionals in a meeting"
                className="w-full h-64 lg:h-auto object-cover rounded-2xl shadow-2xl lg:min-h-[600px]"
              />
            </div>
            <div className="w-full">
              <h2 className="text-3xl md:text-4xl font-bold text-[#383D3B] mb-6">Common Questions Answered</h2>
              <p className="text-lg text-gray-600 mb-8">
                Everything you need to know before starting your trademark journey
              </p>
              <Accordion type="single" collapsible className="space-y-4">
                {[
                  { q: "What is the typical timeline for trademark registration?", a: "Most applications reach registration in eight to twelve months, depending on USPTO workload. If the examiner issues an office action, the timeline may extend. Our team works to resolve issues quickly and keep your application on track." },
                  { q: "What types of marks can be registered?", a: "You can register business names, product names, logos, slogans, and even non-traditional marks like sounds or colors that serve as source identifiers. Our attorneys evaluate your mark's eligibility during the initial consultation." },
                  { q: "Is hiring an attorney required for trademark filing?", a: "While not legally mandatory for U.S.-based applicants, working with a licensed attorney significantly increases your chances of approval and ensures your application is prepared correctly from the start." },
                  { q: "What are the costs involved?", a: "Our service packages start at forty-five dollars plus the USPTO filing fee of two hundred fifty to three hundred fifty dollars per class. We publish all pricing upfront so there are never any hidden charges." },
                  { q: "What does a comprehensive trademark search cover?", a: "We search the USPTO database, state trademark registries, common-law sources, domain name records, and social media platforms to build a complete conflict picture before you file." },
                  { q: "Can I register a mark similar to an existing one?", a: "Possibly, but it depends on the relatedness of the goods or services and the degree of similarity. Our pre-filing analysis assesses the likelihood-of-confusion factors and advises whether it is safe to proceed." }
                ].map((faq, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="bg-white border border-gray-200 rounded-lg px-6">
                    <AccordionTrigger className="text-left font-semibold text-[#383D3B] hover:text-[#99AA38]">
                      <div className="flex items-center space-x-3">
                        <span className="w-8 h-8 rounded-full bg-[#e8edc7] text-[#99AA38] flex items-center justify-center text-sm font-bold flex-shrink-0">Q</span>
                        <span>{faq.q}</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600 pl-11 pt-2">{faq.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              <div className="mt-8">
                <Button onClick={onOpenPopup} className="bg-[#99AA38] hover:bg-[#889930] text-white px-8 py-6 text-lg font-semibold">
                  Still Have Questions? Reach Out
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1772541800741-0ff177b2c282?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNpdHlzY2FwZSUyMHNreWxpbmUlMjBuaWdodCUyMGRhcmt8ZW58MHx8fHwxNzc2Mjg2OTQzfDA&ixlib=rb-4.1.0&q=85" alt="City skyline at dusk" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#383D3B]/80"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6">Take the First Step Toward Federal Protection</h2>
          <p className="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">
            Join the thousands of businesses that trust IP Protection Office to defend what they have built. Start your application today.
          </p>
          <Button size="lg" onClick={onOpenPopup} className="bg-[#99AA38] hover:bg-[#889930] text-white text-lg px-8 py-6 font-semibold">
            Register Your Trademark Now
          </Button>
        </div>
      </section>
    </div>
  );
};
